import { Service, UploadFile } from '@anispi-reporting-system/types';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { AppBar, Box, Card, Container, Grid, Tab, Tabs, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProgressDialog } from './ProgressDialog';
import { Settings } from './Settings';
import { UploadCsvFileProperties } from './UploadCsvFileProperties';
import { UploadCsvFiles } from './UploadCsvFiles';
import { useSnackbarMessage } from '../../hooks/useSnackbarMessage';
import { checkFiles, sendFiles } from '../../lib/Api';
import { Button } from '../Button';
import { ErrorMessages } from '../ErrorMessages';
import { SnackbarMessage } from '../SnackbarMessage';

const Form = () => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [invalid, setInvalid] = useState(true);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [readySentFileCount, setReadySentFileCount] = useState(0);
  const [sentFileCount, setSentFileCount] = useState(0);

  const [uploadFilesErrorMessages, setUploadFilesErrorMessages] = useState<string[]>([]);
  const [notificationPermission, setNotificationPermission] = useState<typeof Notification.permission | undefined>(undefined);

  const { onClose, snackbarMessage, setSnackbarMessage } = useSnackbarMessage();

  const heandleClear = async (sent = false) => {
    if (sent) {
      const unsentFiles = uploadFiles.filter(({ sent: uploadFileSent }) => uploadFileSent === false);
      setUploadFiles(unsentFiles);

      return;
    }

    setUploadFiles([]);
  };

  const handleChangeTab = (_event: React.SyntheticEvent, newCurrentTab: number) => {
    setCurrentTab(newCurrentTab);
  };

  const handleFileCheck = async (uploadedFile: UploadFile) => {
    if (uploadedFile.companyNo === '') {
      return undefined;
    }

    if (uploadedFile.officeNo === '') {
      return undefined;
    }

    if (uploadedFile.service === '') {
      return undefined;
    }

    try {
      const checkFileResult = await checkFiles({
        companyNo: uploadedFile.companyNo,
        fileName: uploadedFile.key,
        officeNo: uploadedFile.officeNo,
        service: uploadedFile.service,
      });

      return {
        ...uploadedFile,
        check: checkFileResult.success,
        errorMessages: checkFileResult.errorMessages || [],
      };
    } catch {
      return {
        ...uploadedFile,
        check: undefined,
        errorMessages: ['エラーが発生しました。'],
      };
    }
  };

  const handleFilesCheck = async () => {
    if (uploadFiles.length === 0) {
      return;
    }

    const fileCheckTargets = uploadFiles.filter(({ check }) => check !== true);

    if (fileCheckTargets.length === 0) {
      return;
    }

    setChecking(true);
    const filesCheckResult = (await Promise.allSettled(fileCheckTargets.map(fileCheckTarget => handleFileCheck(fileCheckTarget)))).map(
      fileCheckTarget => (fileCheckTarget.status === 'fulfilled' ? fileCheckTarget.value : undefined),
    );

    const newUploadFiles = uploadFiles.map(uploadFile => {
      const fileCheck = filesCheckResult.find(fileCheckResult => fileCheckResult?.key === uploadFile.key);

      return {
        ...uploadFile,
        ...(fileCheck && {
          check: fileCheck.check,
          errorMessages: fileCheck.errorMessages,
        }),
      };
    });

    setUploadFiles(newUploadFiles);
    setChecking(false);

    if (filesCheckResult.find(fileCheckResult => fileCheckResult && fileCheckResult.errorMessages.length > 0)) {
      setSnackbarMessage({
        message: '一括チェックが完了しましたが、エラーのファイルがあります。',
        open: true,
        severity: 'warning',
      });
    } else {
      setSnackbarMessage({
        message: '一括チェックが完了しました。',
        open: true,
        severity: 'success',
      });
    }
  };

  const isInvalidCheck = useCallback(() => {
    // 事業所番号の重複チェック
    const duplucateOfficeNo = Array.from(
      new Set(
        uploadFiles
          .filter(({ key, officeNo }) =>
            uploadFiles.find(uploadFile => uploadFile.key !== key && officeNo !== '' && uploadFile.officeNo === officeNo),
          )
          .map(({ officeNo }) => officeNo),
      ),
    );

    if (duplucateOfficeNo.length > 0) {
      setUploadFilesErrorMessages(['重複している事業所番号があります。', duplucateOfficeNo.join(' / ')]);
      return true;
    }

    const uncheckedFiles = uploadFiles.filter(({ check }) => check !== true);

    if (uncheckedFiles.length > 0) {
      return true;
    }

    return false;
  }, [uploadFiles]);

  const existSendable = useMemo(() => uploadFiles.filter(({ check, sent }) => check && !sent).length > 0, [uploadFiles]);
  const existCheckable = useMemo(() => uploadFiles.filter(({ check }) => !check).length > 0, [uploadFiles]);

  const updateSentFileCount = (sentCount: number) => {
    setSentFileCount(prev => prev + sentCount);
  };

  const handleSend = async () => {
    if (existSendable === false) {
      return;
    }

    setSending(true);
    try {
      // ファイルのアップロード
      const params = uploadFiles
        .filter(({ check, sent }) => check && !sent)
        .map(({ companyNo, officeNo, key, service }) => ({
          companyNo,
          fileName: key,
          officeNo,
          service: service as Service,
        }));

      setReadySentFileCount(params.length);
      setSentFileCount(0);
      const sendFilesResult = await sendFiles(params, updateSentFileCount);

      const newUploadFiles = uploadFiles.map(file => {
        const sendFile = sendFilesResult.results.find(result => result.key === file.key);

        return {
          ...file,
          errorMessages: sendFile ? sendFile.errorMessages : file.errorMessages,
          sent: sendFile ? sendFile.success : file.sent,
        };
      });

      setUploadFiles(newUploadFiles);

      if (sendFilesResult.results.find(sendFileResult => sendFileResult && sendFileResult.errorMessages.length > 0)) {
        setSnackbarMessage({
          message: 'ファイルの送信が完了しましたが、エラーのファイルがあります。',
          open: true,
          severity: 'warning',
        });
      } else {
        setSnackbarMessage({
          message: 'ファイルの送信が完了しました。',
          open: true,
          severity: 'success',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setSnackbarMessage({
        message: 'エラーが発生しました',
        open: true,
        severity: 'error',
      });
    } finally {
      if (notificationPermission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification('国保連伝送用CSVファイル（SEデータ）一括アップロード', {
          body: `ファイルの送信が完了しました。${new Date()}`,
        });
      }

      setSending(false);
    }
  };

  const handleChange = useCallback(
    (fileKey: string, value: Partial<Pick<UploadFile, 'officeNo' | 'companyNo' | 'service'>>) => {
      const newUploadFiles = uploadFiles.map(file => ({
        ...file,
        ...(file.key === fileKey && value),
        check: file.key === fileKey ? undefined : file.check,
        errorMessages: [],
        sent: file.key === fileKey ? false : file.sent,
      }));
      setUploadFiles(newUploadFiles);
    },
    [uploadFiles],
  );

  const handleDelete = useCallback(
    (key: string) => {
      const newUploadFiles = uploadFiles.filter(({ key: oldKey }) => oldKey !== key);
      setUploadFiles(newUploadFiles);
    },
    [uploadFiles],
  );

  useEffect(() => {
    setUploadFilesErrorMessages([]);
    setInvalid(isInvalidCheck());
  }, [isInvalidCheck, uploadFiles]);

  useEffect(() => {
    if (Notification) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const updateNotificationPermission = async () => {
    if (Notification) {
      if (notificationPermission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  };

  return (
    <Container maxWidth="lg" className="space-y-8 pt-4">
      <ProgressDialog open={sending} fileCount={readySentFileCount} sentCount={sentFileCount} />
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Typography variant="h4" className="text-center text-base">
            アニスピホールディングス用
            <br />
            国保連伝送用CSVファイル（SEデータ）一括アップロード
          </Typography>
        </Grid>
      </Grid>

      <AppBar position="static" color="inherit">
        <Tabs value={currentTab} onChange={handleChangeTab} aria-label="full width tabs" textColor="inherit" variant="fullWidth">
          <Tab label="一括アップロード" />
          <Tab label="設定" />
        </Tabs>
      </AppBar>

      {currentTab === 1 && <Settings />}

      {currentTab === 0 && (
        <Box className="mx-auto w-full">
          {notificationPermission !== undefined && (
            <Box className="pb-6">
              {notificationPermission === 'default' ? (
                <Button color="primary" variant="outlined" onClick={() => updateNotificationPermission()}>
                  <NotificationsNoneIcon />
                  通知を許可する
                </Button>
              ) : (
                <Box color="primary.main">
                  {notificationPermission === 'granted' ? (
                    <>
                      <NotificationsActiveIcon />
                      <Box className="pl-2" display="inline">
                        通知ON
                      </Box>
                    </>
                  ) : (
                    <>
                      <NotificationsOffIcon />
                      <Box className="pl-2" display="inline">
                        通知OFF
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

          {uploadFiles.length === 0 ? (
            <Box className="pb-6">
              <UploadCsvFiles setSnackbarMessage={setSnackbarMessage} setUploadFiles={setUploadFiles} />
            </Box>
          ) : (
            <Box className="space-y-4">
              <Box className="pb-2">
                アップロードの結果(ファイル数: {uploadFiles.length} / チェック済み
                {uploadFiles.filter(({ check }) => check === true).length} / 送信済み
                {uploadFiles.filter(({ sent }) => sent === true).length})
              </Box>

              <ErrorMessages errorMessages={uploadFilesErrorMessages} />

              <Card variant="outlined" className="h-96 space-y-4 !overflow-y-scroll p-4">
                {uploadFiles.map(uploadFile => (
                  <UploadCsvFileProperties key={uploadFile.key} file={uploadFile} handleChange={handleChange} handleDelete={handleDelete} />
                ))}
              </Card>
              <Box className="flex flex-row justify-center space-x-4 py-4">
                <Button color="error" variant="contained" onClick={() => heandleClear()} disabled={uploadFiles.length === 0}>
                  全てクリア
                </Button>

                <Button color="error" variant="outlined" onClick={() => heandleClear(true)} disabled={uploadFiles.length === 0}>
                  送信済みのみクリア
                </Button>

                <Button
                  className="w-40"
                  variant="outlined"
                  loading={checking}
                  onClick={handleFilesCheck}
                  disabled={invalid === false || sending || !existCheckable}
                >
                  一括チェック
                </Button>
                <Button className="w-40" variant="outlined" loading={sending} onClick={handleSend} disabled={!existSendable || checking}>
                  ファイルの送信
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      <SnackbarMessage
        open={snackbarMessage.open}
        severity={snackbarMessage.severity}
        message={snackbarMessage.message}
        onClose={onClose}
      />
    </Container>
  );
};

export { Form };
